import React, { useState, useEffect } from 'react';
import { media } from 'helpers/MediaHelper';

import Select from 'components/ui/SelectField';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import CtaText from 'components/ui/CtaCard';
import { default as AntDrawer } from 'components/ui/Drawer';
import DrawerBody from 'components/ui/DrawerBody';
import DrawerFooter from 'components/ui/DrawerFooter';
import CtaPrimary from 'components/ui/CtaPrimary';

import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';
import { deepDiver } from 'helpers/utilities';

import { LinkServiceRequests } from './LinkServiceRequests';
import { LinkProject } from './LinkProject';
import { InternalComments } from './InternalComments';
import { ViewPurchaseOrderItemsList } from './ViewPurchaseOrderItemsList';
import { EditApproverPurchaseOrderDrawer } from './EditApproverPurchaseOrderDrawer';
import { DenyPurchaseOrderForm } from './DenyPurchaseOrder';
import { ViewPurchaseOrderHeader } from './shared/ViewPurchaseOrderHeader';
import { ViewPurchaseOrderDetails } from './shared/ViewPurchaseOrderDetails';

import Lottie from 'react-lottie';

import cautionAnimationData from 'assets/caution.json';
import errorAnimationData from 'assets/error.json';
import loadingAnimationData from 'assets/loading.json';
import successAnimationData from 'assets/success.json';

import DrawerSuccessMessages from 'components/ui/DrawerSuccessMessages';

const StyledForm = styled(Form)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: ${props => (props.readOnly ? 'start' : 'space-between')};
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 20px;
`;

const StyledRowItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0px 12px;
  text-align: ${props => props.textAlign || 'left'};
  color: ${props => props.color || 'black'};
  width: ${props => props.width || 'auto'};
  ${media.mobile`
    margin-bottom: 15px;
    padding: 0px;
  `};
`;

const StyledRowItemLabel = styled.div`
  margin-bottom: 5px;
  color: ${colors.FONT_GRAY};
`;

const StyledError = styled.div`
  color: ${colors.YUHU_RED};
`;

const VIEW_APPROVAL_PAGE = 'view_approval_page';
const CONFIRM_APPROVAL_PAGE = 'confirm_approval_page';
const SUCCESS_APPROVAL_PAGE = 'success_approval_page';
const READ_ONLY_APPROVAL_PAGE = 'read_only_approval_page';

const VIEW_DENIAL_PAGE = 'view_denial_page';
const SUCCESS_DENIAL_PAGE = 'success_denial_page';
const READ_ONLY_DENIAL_PAGE = 'read_only_denial_page';

const ApprovePurchaseOrderSchema = Yup.object().shape({
  expense_type_id: Yup.string().required(getTranslatedString('required')),
});

export const ApproverViewPurchaseOrder = ({
  purchaseOrder,
  toggleViewPurchaseOrder,
  glAccounts,
  expenseTypes,
  approvePurchaseOrder,
  getPurchaseOrder,
  getPurchaseOrderStats,
  fetchPurchaseOrders,
  downloadPdf,
  getUnits,
  createPurchaseOrderComment,
  denyPurchaseOrder,
  attachPurchaseOrderServiceRequest,
  removePurchaseOrderServiceRequest,
  vendorFullInfo,
  postProject,
  attachPurchaseOrderProject,
  removePurchaseOrderProject,
  properties,
  units,
}) => {
  const [approvalState, setApprovalState] = useState(VIEW_APPROVAL_PAGE);
  const [toggleEditPurchaseOrder, setToggleEditPurchaseOrder] = useState(false);
  const [purchaseOrderItems, setPurchaseOrderItems] = useState([]);
  const [purchaseOrderItemsModified, setPurchaseOrderItemsModified] = useState(false);
  const [isUpdatingPurchaseOrder, setIsUpdatingPurchaseOrder] = useState(false);
  const [toggleError, setToggleError] = useState(false);

  useEffect(
    () => {
      if (purchaseOrder.purchase_order_items) {
        setPurchaseOrderItems(purchaseOrder.purchase_order_items);
        if (
          purchaseOrder.purchase_order_items.some(
            item =>
              ['modified', 'newly_added'].includes(item.state) &&
              purchaseOrder.status === 'pending_approval'
          )
        ) {
          setPurchaseOrderItemsModified(true);
        }
      }

      if ([SUCCESS_APPROVAL_PAGE, SUCCESS_DENIAL_PAGE].includes(approvalState)) {
        return;
      }

      switch (purchaseOrder.status) {
        case 'approved':
          setApprovalState(READ_ONLY_APPROVAL_PAGE);
          break;
        case 'denied':
          setApprovalState(READ_ONLY_DENIAL_PAGE);
          break;
        default:
          break;
      }
    },
    [
      approvalState,
      purchaseOrder,
      setApprovalState,
      purchaseOrder.purchase_order_items,
      setPurchaseOrderItems,
      setPurchaseOrderItemsModified,
      purchaseOrder.status,
    ]
  );

  const handleOpeningEditPurchaseOrder = () => {
    getUnits(purchaseOrder.property_id, { for_multiselect: true });
    setToggleEditPurchaseOrder(true);
  };

  const handleSelectServiceRequest = serviceRequest => {
    let params = {
      service_request_id: serviceRequest.id,
    };
    attachPurchaseOrderServiceRequest(params, purchaseOrder.id)
      .then(res => {
        getPurchaseOrder(purchaseOrder.id);
        fetchPurchaseOrders({
          updateList: true,
        });
      })
      .catch(err => {
        console.error(err);
      });
  };

  const handleUnlinkServiceRequest = serviceRequestId => {
    removePurchaseOrderServiceRequest(purchaseOrder.id, serviceRequestId)
      .then(res => {
        getPurchaseOrder(purchaseOrder.id);
        fetchPurchaseOrders({
          updateList: true,
        });
      })
      .catch(err => {
        console.error(err);
      });
  };

  const handleSelectProject = projectId => {
    attachPurchaseOrderProject(purchaseOrder.id, projectId)
      .then(res => {
        getPurchaseOrder(purchaseOrder.id);
      })
      .catch(err => {
        console.error(err);
      });
  };

  const handleUnlinkProject = projectId => {
    removePurchaseOrderProject(purchaseOrder.id, projectId)
      .then(res => {
        getPurchaseOrder(purchaseOrder.id);
      })
      .catch(err => {
        console.error(err);
      });
  };

  const handlePropertyChange = value => {
    getUnits(value, { for_multiselect: true });
  };

  const serviceRequestIds = purchaseOrder.maintenance_tickets.map(maintenance_ticket =>
    String(maintenance_ticket.id)
  );

  const readOnly =
    approvalState === READ_ONLY_APPROVAL_PAGE ||
    approvalState === READ_ONLY_DENIAL_PAGE ||
    purchaseOrder.status === 'needs_review';

  const toggleSuccessDrawer = () => {
    setApprovalState(READ_ONLY_DENIAL_PAGE);
  };

  const resetError = () => {
    setToggleError(false);
  };

  if (isUpdatingPurchaseOrder) {
    return renderLoading();
  }

  if (approvalState === SUCCESS_DENIAL_PAGE) {
    return renderSuccess(toggleSuccessDrawer);
  }

  if (toggleError) {
    return renderError(resetError);
  }

  return (
    <React.Fragment>
      <Formik
        initialValues={{
          expense_type_id: purchaseOrder.expense_type_id || '',
          expense_type_name: purchaseOrder.expense_type_name || '',
          internal_comments: '',
          purchase_order_items: purchaseOrder.purchase_order_items || [],
          gl_accounts_error: false,
          external_vendor_street: deepDiver(vendorFullInfo, 'address.street'),
          external_vendor_postal_code: deepDiver(vendorFullInfo, 'address.postal_code'),
          external_vendor_city: deepDiver(vendorFullInfo, 'address.city'),
          external_vendor_province: deepDiver(vendorFullInfo, 'address.province'),
        }}
        validationSchema={ApprovePurchaseOrderSchema}
        onSubmit={values => {
          setIsUpdatingPurchaseOrder(true);
          approvePurchaseOrder(values, purchaseOrder.id)
            .then(res => {
              setIsUpdatingPurchaseOrder(false);
              fetchPurchaseOrders({
                updateList: true,
              });
              getPurchaseOrderStats();
              getPurchaseOrder(purchaseOrder.id);
              setApprovalState(SUCCESS_APPROVAL_PAGE);
            })
            .catch(err => {
              setToggleError(true);
              console.error(err);
            });
        }}
      >
        {({ values, setFieldValue, submitForm, validateForm, errors, touched }) => {
          const handleSingleCommentChange = event => {
            setFieldValue('internal_comments', event.currentTarget.value);
          };

          const checkGlAccountIds = purchaseOrderItems => {
            if (
              purchaseOrderItems.some(
                item =>
                  item.gl_account_id === null &&
                  item.gl_account_name === null &&
                  item.gl_account_code === null
              )
            ) {
              setFieldValue('gl_accounts_error', true);
            } else {
              setFieldValue('gl_accounts_error', false);
            }
          };

          const handleGlAccountChange = (value, index) => {
            const glAccount = glAccounts.find(account => account.hmy === value);
            const glAccountName = glAccount.description;
            const glAccountCode = glAccount.gl_account;

            let newPurchaseOrderItems = [...purchaseOrderItems];
            newPurchaseOrderItems[index].gl_account_id = value;
            newPurchaseOrderItems[index].gl_account_name = glAccountName;
            newPurchaseOrderItems[index].gl_account_code = glAccountCode;
            setPurchaseOrderItems(newPurchaseOrderItems);
            setFieldValue('purchase_order_items', newPurchaseOrderItems);
            checkGlAccountIds(newPurchaseOrderItems);
          };

          return (
            <StyledForm readOnly={readOnly}>
              {[VIEW_APPROVAL_PAGE, READ_ONLY_APPROVAL_PAGE, READ_ONLY_DENIAL_PAGE].includes(
                approvalState
              ) && (
                <React.Fragment>
                  <ViewPurchaseOrderHeader
                    purchaseOrder={purchaseOrder}
                    toggleViewPurchaseOrder={toggleViewPurchaseOrder}
                  />
                  <DrawerBody>
                    <div>
                      <ViewPurchaseOrderDetails purchaseOrder={purchaseOrder} />
                      <StyledRow>
                        <StyledRowItem width="100%">
                          <StyledRowItemLabel>
                            {getTranslatedString(
                              'landlordPortal.approverViewPurchaseOrder.expenseType'
                            )}
                          </StyledRowItemLabel>
                          <Select
                            name="expense_type_id"
                            showSearch
                            placeholder={getTranslatedString(
                              'landlordPortal.purchaseOrderForm.select'
                            )}
                            size="large"
                            value={values.expense_type_id}
                            onChange={value => {
                              const expenseTypeName = expenseTypes.find(
                                type => type.hmy === parseInt(value)
                              )?.expense_type;
                              setFieldValue('expense_type_id', value);
                              setFieldValue('expense_type_name', expenseTypeName);
                              checkGlAccountIds(purchaseOrderItems);
                            }}
                            disabled={readOnly}
                            filterOption={(input, option) =>
                              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                          >
                            {expenseTypes &&
                              expenseTypes.map((expenseType, i) => (
                                <Select.Option
                                  value={`${expenseType.hmy}`}
                                  key={`${expenseType.hmy}_${i}`}
                                >
                                  {expenseType.expense_type}
                                </Select.Option>
                              ))}
                          </Select>
                          {purchaseOrder.status === 'pending_approval' &&
                            errors.expense_type_id && (
                              <StyledError>{errors.expense_type_id}</StyledError>
                            )}
                        </StyledRowItem>
                      </StyledRow>
                      {purchaseOrder.status === 'pending_approval' &&
                        values.gl_accounts_error && (
                          <StyledRow>
                            <StyledRowItem>
                              <StyledError>
                                {getTranslatedString(
                                  'landlordPortal.approverViewPurchaseOrder.requireGeneralLdgerCode'
                                )}
                              </StyledError>
                            </StyledRowItem>
                          </StyledRow>
                        )}
                      <ViewPurchaseOrderItemsList
                        purchaseOrderItems={purchaseOrderItems}
                        readOnly={readOnly}
                        handleOpeningEditPurchaseOrder={handleOpeningEditPurchaseOrder}
                        purchaseOrderItemsModified={purchaseOrderItemsModified}
                        values={values}
                        glAccounts={glAccounts}
                        handleGlAccountChange={handleGlAccountChange}
                        purchaseOrderStatus={purchaseOrder.status}
                      />
                      <AntDrawer
                        maskClosable={false}
                        keyboard
                        visible={toggleEditPurchaseOrder}
                        onClose={toggleEditPurchaseOrder}
                        closable={false}
                        width={600}
                      >
                        <EditApproverPurchaseOrderDrawer
                          purchaseOrder={purchaseOrder}
                          toggleEditPurchaseOrder={setToggleEditPurchaseOrder}
                          getPurchaseOrderStats={getPurchaseOrderStats}
                          fetchPurchaseOrders={fetchPurchaseOrders}
                          setPurchaseOrderItemsModified={setPurchaseOrderItemsModified}
                        />
                      </AntDrawer>
                    </div>
                    <div>
                      <LinkServiceRequests
                        handleSelectServiceRequest={handleSelectServiceRequest}
                        handleUnlinkServiceRequest={handleUnlinkServiceRequest}
                        propertyId={purchaseOrder.property_id}
                        selectedServiceRequests={serviceRequestIds}
                        serviceRequests={purchaseOrder.maintenance_tickets}
                      />
                      <LinkProject
                        handleSelectProject={handleSelectProject}
                        handleUnlinkProject={handleUnlinkProject}
                        selectedProject={purchaseOrder.project}
                        propertyId={purchaseOrder.property_id}
                        properties={properties}
                        units={units}
                        postProject={postProject}
                        fetchUnitsOfProperty={handlePropertyChange}
                        viewMode
                      />
                      {purchaseOrder.status === 'pending_approval' ? (
                        <InternalComments
                          handleCommentChange={handleSingleCommentChange}
                          values={values}
                          comments={purchaseOrder.internal_comments}
                          showOnlyComments={approvalState === READ_ONLY_APPROVAL_PAGE}
                          noBackground
                        />
                      ) : (
                        <Formik
                          initialValues={{
                            internal_comments: '',
                          }}
                          onSubmit={(values, { resetForm }) => {
                            createPurchaseOrderComment(values, purchaseOrder.id)
                              .then(res => {
                                getPurchaseOrder(purchaseOrder.id);
                                resetForm();
                              })
                              .catch(err => {
                                console.error(err);
                              });
                          }}
                        >
                          {({ values, setFieldValue }) => {
                            const handleCommentChange = event => {
                              setFieldValue('internal_comments', event.currentTarget.value);
                            };
                            return (
                              <Form>
                                <InternalComments
                                  handleCommentChange={handleCommentChange}
                                  values={values}
                                  comments={purchaseOrder.internal_comments}
                                  canAddNewNotes
                                  noBackground
                                />
                              </Form>
                            );
                          }}
                        </Formik>
                      )}
                    </div>
                  </DrawerBody>
                  {approvalState === VIEW_APPROVAL_PAGE && (
                    <DrawerFooter
                      primaryActionDisabled={purchaseOrder.status === 'needs_review'}
                      primaryActionText={getTranslatedString(
                        'landlordPortal.viewPurchaseOrderDrawer.approve'
                      )}
                      onPrimaryActionClick={() => {
                        if (values.gl_accounts_error) {
                          return;
                        }
                        validateForm(values).then(res => {
                          if (!('expense_type_id' in res)) {
                            setApprovalState(CONFIRM_APPROVAL_PAGE);
                          }
                        });
                      }}
                      onSecondaryActionClick={() => setApprovalState(VIEW_DENIAL_PAGE)}
                      secondaryActionText={getTranslatedString(
                        'landlordPortal.viewPurchaseOrderDrawer.deny'
                      )}
                      topTitleText={
                        purchaseOrderItemsModified
                          ? getTranslatedString(
                              'landlordPortal.viewPurchaseOrderDrawer.thereHasBeenModifications'
                            )
                          : purchaseOrder.status === 'needs_review'
                            ? getTranslatedString(
                                'landlordPortal.viewPurchaseOrderDrawer.sinceYouModified'
                              )
                            : null
                      }
                    />
                  )}
                  {approvalState === READ_ONLY_DENIAL_PAGE && (
                    <DrawerFooter
                      noAction
                      descriptionTitle={getTranslatedString(
                        'landlordPortal.denyPurchaseOrder.deniedFooterTitle'
                      )}
                      descriptionSubtitle={purchaseOrder.rejected_reason}
                    />
                  )}
                  {approvalState === READ_ONLY_APPROVAL_PAGE && (
                    <DrawerFooter
                      primaryActionText={getTranslatedString(
                        'landlordPortal.viewPurchaseOrderDrawer.savePdf'
                      )}
                      onPrimaryActionClick={() => downloadPdf(purchaseOrder.id)}
                    />
                  )}
                </React.Fragment>
              )}
              {approvalState === CONFIRM_APPROVAL_PAGE &&
                renderApproval(submitForm, () => setApprovalState(VIEW_APPROVAL_PAGE))}
              {approvalState === SUCCESS_APPROVAL_PAGE &&
                renderSuccess(
                  () => setApprovalState(READ_ONLY_APPROVAL_PAGE),
                  purchaseOrder.status === 'pending_approval'
                )}
              {approvalState === VIEW_DENIAL_PAGE && (
                <DenyPurchaseOrderForm
                  setApprovalState={setApprovalState}
                  denyPurchaseOrder={denyPurchaseOrder}
                  getPurchaseOrder={getPurchaseOrder}
                  purchaseOrder={purchaseOrder}
                  fetchPurchaseOrders={fetchPurchaseOrders}
                  getPurchaseOrderStats={getPurchaseOrderStats}
                  setToggleError={setToggleError}
                  setIsUpdatingPurchaseOrder={setIsUpdatingPurchaseOrder}
                />
              )}
            </StyledForm>
          );
        }}
      </Formik>
    </React.Fragment>
  );
};

const renderLoading = () => {
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: loadingAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
      headerText={getTranslatedString('landlordPortal.addPurchaseOrderDrawer.loadingHeader')}
      bodyText={getTranslatedString('globalDrawer.loadingBody')}
    />
  );
};

const renderError = resetError => {
  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: errorAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
      headerText={getTranslatedString('landlordPortal.addPurchaseOrderDrawer.errorHeader')}
      bodyText={getTranslatedString('globalDrawer.errorBody')}
      cta={
        <CtaPrimary onClick={resetError}>{getTranslatedString('globalDrawer.goBack')}</CtaPrimary>
      }
    />
  );
};

const renderSuccess = (handleClose, isApproved = false) => {
  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: successAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  let headerText = getTranslatedString('landlordPortal.addPurchaseOrderDrawer.successHeader');
  let bodyText = getTranslatedString('globalDrawer.successBody');
  if (isApproved) {
    headerText = getTranslatedString('landlordPortal.addPurchaseOrderDrawer.success');
    bodyText = getTranslatedString('landlordPortal.viewPurchaseOrderDrawer.purchaseOrderApproved');
  }

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
      headerText={headerText}
      bodyText={bodyText}
      cta={<CtaPrimary onClick={handleClose}>{getTranslatedString('close')}</CtaPrimary>}
    />
  );
};

const renderApproval = (submitForm, approvePO) => {
  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: cautionAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
      headerText={getTranslatedString(
        'landlordPortal.viewPurchaseOrderDrawer.approvePurchaseOrder'
      )}
      bodyText={getTranslatedString('landlordPortal.viewPurchaseOrderDrawer.youCannotModify')}
      cta={
        <CtaPrimary onClick={submitForm}>
          {getTranslatedString('landlordPortal.viewPurchaseOrderDrawer.approve')}
        </CtaPrimary>
      }
      secondaryCta={
        <CtaText type="button" onClick={approvePO}>
          {getTranslatedString('close')}
        </CtaText>
      }
    />
  );
};
